import React, { useState, useEffect } from 'react';
import Button from "../../../../components/Button";
import IconButton from "../../../../components/IconButton";
import Checkbox from "../../../../components/Checkbox";
import Input from "../../../../components/Input";
import Select from "../../../../components/Select";
import { faCopy, faTrash } from "@fortawesome/free-solid-svg-icons";
import styles from "./ConditionalTab.module.scss";
import cx from 'classnames';
import {ConditionalGroup, ConditionalLogic, ConditionalRule, FormField} from "../../../../types/ContactForm";

interface ConditionalTabProps {
  config: {
    formFields: FormField[];
    conditionalGroups?: ConditionalGroup[];
    enableConditionalLogic?: boolean;
  };
  updateConfig: (property: string, value: any) => void;
}

const ConditionalTab: React.FC<ConditionalTabProps> = ({ config, updateConfig }) => {
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  // Clean up conditions when fields are deleted
  useEffect(() => {
    if (!config?.formFields) return;

    const availableFieldNames = (config.formFields || [])
      .filter((field: FormField) => ['text', 'email', 'tel', 'number', 'textarea', 'select', 'checkbox', 'radio'].includes(field.type))
      .map((field: FormField) => field.name);

    const currentGroups = config.conditionalGroups || [];
    let hasChanges = false;

    const updatedGroups = currentGroups.map((group: ConditionalGroup) => {
      if (!group.conditionalLogic?.conditions) return group;

      // Remove conditions that reference deleted fields or have empty field values
      const validConditions = group.conditionalLogic.conditions.filter((condition: ConditionalRule) => 
        condition.field && availableFieldNames.includes(condition.field)
      );

      if (validConditions.length !== group.conditionalLogic.conditions.length) {
        hasChanges = true;
        return {
          ...group,
          conditionalLogic: {
            ...group.conditionalLogic,
            conditions: validConditions
          }
        };
      }
      return group;
    });

    if (hasChanges) {
      updateConfig('conditionalGroups', updatedGroups);
    }
  }, [config?.formFields, updateConfig]);

  if (!config || !config.formFields) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading configuration...</p>
      </div>
    );
  }

  const conditionalGroups = config.conditionalGroups || [];
  const enableConditionalLogic = config.enableConditionalLogic || false;

  // Available fields for conditions (excluding file fields as they're complex)
  const availableFields = (config.formFields || []).filter((field: FormField) => 
    ['text', 'email', 'tel', 'number', 'textarea', 'select', 'checkbox', 'radio'].includes(field.type)
  );

  // Create field options for the Select component
  const getFieldOptions = () => {
    const fieldOptions = [
      { value: '', label: 'Feld auswählen...' }
    ];

    if (availableFields.length > 0) {
      availableFields.forEach((field: FormField) => {
        fieldOptions.push({
          value: field.name,
          label: `${field.label} (${field.name})`
        });
      });
    } else {
      // Fallback if no fields available
      fieldOptions.push({
        value: 'no_fields',
        label: 'Keine Felder verfügbar'
      });
    }

    return fieldOptions;
  };

  const operatorOptions = [
    { value: 'equals', label: 'ist gleich' },
    { value: 'not_equals', label: 'ist ungleich' },
    { value: 'contains', label: 'enthält' },
    { value: 'not_contains', label: 'enthält nicht' },
    { value: 'is_empty', label: 'ist leer' },
    { value: 'is_not_empty', label: 'ist nicht leer' },
    { value: 'greater_than', label: 'ist größer als' },
    { value: 'less_than', label: 'ist kleiner als' }
  ];

  const toggleConditionalLogic = (property: string, enabled: boolean) => {
    updateConfig('enableConditionalLogic', enabled);
    if (!enabled) {
      updateConfig('conditionalGroups', []);
    }
  };

  const createNewGroup = () => {
    const newGroup: ConditionalGroup = {
      id: `group_${Date.now()}`,
      title: `Bedingte Gruppe ${conditionalGroups.length + 1}`,
      fields: [],
      conditionalLogic: {
        enabled: true,
        action: 'show',
        operator: 'and',
        conditions: []
      }
    };

    updateConfig('conditionalGroups', [...conditionalGroups, newGroup]);
    setActiveGroup(newGroup.id);
  };

  const updateGroup = (groupId: string, property: string, value: any) => {
    const updatedGroups = conditionalGroups.map((group: ConditionalGroup) => 
      group.id === groupId ? { ...group, [property]: value } : group
    );
    updateConfig('conditionalGroups', updatedGroups);
  };

  const updateGroupLogic = (groupId: string, logic: Partial<ConditionalLogic>) => {
    const updatedGroups = conditionalGroups.map((group: ConditionalGroup) => 
      group.id === groupId 
        ? { ...group, conditionalLogic: { ...group.conditionalLogic!, ...logic } }
        : group
    );
    updateConfig('conditionalGroups', updatedGroups);
  };

  const addCondition = (groupId: string) => {
    const group = conditionalGroups.find((g: ConditionalGroup) => g.id === groupId);
    if (!group || !group.conditionalLogic) return;

    const newCondition: ConditionalRule = {
      id: `condition_${Date.now()}`,
      field: '',
      operator: 'equals',
      value: ''
    };

    const updatedConditions = [...(group.conditionalLogic.conditions || []), newCondition];
    updateGroupLogic(groupId, { conditions: updatedConditions });
  };

  const updateCondition = (groupId: string, conditionId: string, property: string, value: string) => {
    const group = conditionalGroups.find((g: ConditionalGroup) => g.id === groupId);
    if (!group || !group.conditionalLogic) return;

    const updatedConditions = (group.conditionalLogic.conditions || []).map((condition: ConditionalRule) =>
      condition.id === conditionId ? { ...condition, [property]: value } : condition
    );
    updateGroupLogic(groupId, { conditions: updatedConditions });
  };

  const removeCondition = (groupId: string, conditionId: string) => {
    const group = conditionalGroups.find((g: ConditionalGroup) => g.id === groupId);
    if (!group || !group.conditionalLogic) return;

    const updatedConditions = (group.conditionalLogic.conditions || []).filter(
      (condition: ConditionalRule) => condition.id !== conditionId
    );
    updateGroupLogic(groupId, { conditions: updatedConditions });
  };

  const removeGroup = (groupId: string) => {
    const updatedGroups = conditionalGroups.filter((group: ConditionalGroup) => group.id !== groupId);
    updateConfig('conditionalGroups', updatedGroups);
    if (activeGroup === groupId) {
      setActiveGroup(null);
    }
  };

  const duplicateGroup = (groupId: string) => {
    const group = conditionalGroups.find((g: ConditionalGroup) => g.id === groupId);
    if (!group) return;

    const duplicatedGroup: ConditionalGroup = {
      ...group,
      id: `group_${Date.now()}`,
      title: `${group.title} (Kopie)`,
      conditionalLogic: group.conditionalLogic ? {
        ...group.conditionalLogic,
        conditions: (group.conditionalLogic.conditions || []).map((condition: ConditionalRule) => ({
          ...condition,
          id: `condition_${Date.now()}_${Math.random()}`
        }))
      } : undefined
    };

    updateConfig('conditionalGroups', [...conditionalGroups, duplicatedGroup]);
  };

  const updateGroupFields = (groupId: string, fieldId: string, checked: boolean) => {
    const group = conditionalGroups.find((g: ConditionalGroup) => g.id === groupId);
    if (!group) return;

    const updatedFields = checked
      ? [...(group.fields || []), fieldId]
      : (group.fields || []).filter(id => id !== fieldId);
    
    updateGroup(groupId, 'fields', updatedFields);
  };

  return (
    <div className={cx(styles.container)}>
      <div className={cx(styles['info-box'], 'zr-mb-3')}>
        <p>
          Erstellen Sie Gruppen von Feldern, die basierend auf anderen Feldwerten angezeigt .
        </p>
      </div>
      {availableFields.length === 0 && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffeaa7', padding: '10px', marginBottom: '20px' }}>
          <strong>Info:</strong> Keine Formularfelder gefunden.
          Stellen Sie sicher, dass Sie zuerst Felder im "Felder" Tab erstellt haben.
          <br />
          Verfügbare Felder: {(config.formFields || []).length}
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className={cx(styles.section, 'zr-mb-4')}>
        <Checkbox
          property="enableConditionalLogic"
          label="Bedingte Logik aktivieren"
          value={enableConditionalLogic}
          onChange={toggleConditionalLogic}
        />
      </div>

      {!enableConditionalLogic && (
        <div className={cx(styles.disabled)}>
          <p>Aktivieren Sie die bedingte Logik, um Gruppen von Feldern basierend auf Bedingungen zu steuern.</p>
        </div>
      )}

      {enableConditionalLogic && (
        <>
          {/* Groups List */}
          <div className={cx(styles.section, 'zr-mb-4')}>
            <div className={cx(styles.sectionHeader)}>
              <h5>Bedingte Gruppen</h5>
            </div>

            {conditionalGroups.length === 0 && (
              <div className={cx(styles.emptyState)}>
                <p>Noch keine bedingten Gruppen erstellt.</p>
                <p>Klicken Sie auf "Neue Gruppe", um Ihre erste bedingte Gruppe zu erstellen.</p>
              </div>
            )}

            {conditionalGroups.map(group => (
              <div 
                key={group.id}
                className={styles.groupCard}
              >
                <div className={cx(styles.groupHeader)} onClick={() => setActiveGroup(activeGroup === group.id ? null : group.id)}>
                  <div className={cx(styles.groupInfo)}>
                    <h6>{group.title}</h6>
                    <p>{(group.fields || []).length} Feld(er) • {group.conditionalLogic?.conditions?.length || 0} Bedingung(en)</p>
                  </div>
                  <div className={cx(styles.groupActions)}>
                    <IconButton
                      icon={faCopy}
                      color="#666"
                      onClick={() => duplicateGroup(group.id)}
                      title="Gruppe kopieren"
                      classes={cx(styles['copy-field-btn'])}
                    />
                    <IconButton
                      icon={faTrash}
                      color="#666"
                      onClick={() => removeGroup(group.id)}
                      title="Gruppe löschen"
                      classes={cx(styles['remove-field-btn'])}
                    />
                  </div>
                </div>

                <div className={cx(styles.groupContent, activeGroup === group.id && styles.active)}>
                    {/* Group Settings */}
                    <div className={cx(styles.groupSettings)}>
                      <Input
                        property="title"
                        placeholder="Gruppenname"
                        type="text"
                        value={group.title}
                        onChange={(property, value) => updateGroup(group.id, 'title', value)}
                      />
                    </div>

                    {/* Field Selection */}
                    <div className={cx(styles.fieldSelection)}>
                      <h6>Felder in dieser Gruppe</h6>
                      <div className={cx(styles.fieldGrid)}>
                        {(config.formFields || []).map(field => (
                          <Checkbox
                            key={field.id}
                            property={`field-${field.id}`}
                            label={`${field.label}`}
                            value={(group.fields || []).includes(field.id)}
                            onChange={(property, checked) => updateGroupFields(group.id, field.id, checked)}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Conditional Logic */}
                    <div className={cx(styles.conditionalLogic)}>
                      <h6>Bedingungen</h6>
                      
                      <div className={cx(styles.logicSettings)}>
                        <Select
                          property="action"
                          placeholder="Aktion"
                          value={group.conditionalLogic?.action || 'show'}
                          options={[
                            { value: 'show', label: 'Gruppe anzeigen wenn' },
                            { value: 'hide', label: 'Gruppe ausblenden wenn' }
                          ]}
                          searchable={false}
                          onSelect={(property, value) => updateGroupLogic(group.id, { action: value as 'show' | 'hide' })}
                        />

                        {group.conditionalLogic && (group.conditionalLogic.conditions || []).length > 1 && (
                          <Select
                            property="operator"
                            placeholder="Verknüpfung"
                            value={group.conditionalLogic.operator}
                            searchable={false}
                            options={[
                              { value: 'and', label: 'ALLE Bedingungen müssen erfüllt sein (UND)' },
                              { value: 'or', label: 'EINE Bedingung muss erfüllt sein (ODER)' }
                            ]}
                            onSelect={(property, value) => updateGroupLogic(group.id, { operator: value as 'and' | 'or' })}
                          />
                        )}
                      </div>

                      {/* Conditions */}
                      <div className={cx(styles.conditions)}>
                        {(group.conditionalLogic?.conditions || []).map((condition, index) => (
                          <div key={condition.id} className={cx(styles.conditionRow)}>
                            {index > 0 && (
                              <div className={cx(styles.operator)}>
                                {group.conditionalLogic?.operator === 'and' ? 'UND' : 'ODER'}
                              </div>
                            )}
                            
                            <div className={cx(styles.conditionFields)}>
                              <Select
                                property="field"
                                placeholder="Feld auswählen"
                                value={condition.field}
                                options={getFieldOptions()}
                                onSelect={(property, value) => updateCondition(group.id, condition.id, 'field', value)}
                                searchable={false}
                              />

                              <Select
                                property="operator"
                                placeholder="Operator"
                                value={condition.operator}
                                options={operatorOptions}
                                onSelect={(property, value) => updateCondition(group.id, condition.id, 'operator', value)}
                                searchable={false}
                              />

                              {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                                <Input
                                  property="value"
                                  placeholder="Wert eingeben..."
                                  type="text"
                                  value={condition.value}
                                  onChange={(property, value) => updateCondition(group.id, condition.id, 'value', value)}
                                />
                              )}

                              <IconButton
                                icon={faTrash}
                                color="#666666"
                                onClick={() => removeCondition(group.id, condition.id)}
                                title="Bedingung löschen"
                              />
                            </div>
                          </div>
                        ))}

                        <Button
                          text="Bedingung hinzufügen"
                          fn={() => addCondition(group.id)}
                          hierarchy="secondary"
                        />
                      </div>
                    </div>
                  </div>
              </div>
            ))}
          </div>
        </>
      )}
      <Button
          text="Neue Gruppe"
          fn={createNewGroup}
          hierarchy="primary"
      />
    </div>
  );
};

export default ConditionalTab; 